import { isBlockEnabled } from "@/src/lib/blocks/utils";
import { getNestedValue, hasValue } from "@/src/lib/utils/object-utils";
import { type BlockConfigFile } from "@/src/models/blocks/block-config";
import { BlockKey, BlockType, getBlockTypeFromKey } from "@/src/models/blocks/block-types";
import { getBlockKeysForClassification } from "@/src/models/blocks/enriched-content";
import { type File } from "@/src/models/file";
import { type Section } from "@/src/models/section";

type HasValueAtPath = (path: string) => boolean;

export const AutoIntakeFileCheck = {
  NO_CROPPED_PAGES: {
    failureReason: "cropped_pages",
    passes: (file: Pick<File, "croppedPageIndices"> | null | undefined) =>
      (file?.croppedPageIndices?.length ?? 0) === 0,
  },
} as const;

export type AutoIntakeFileCheck = (typeof AutoIntakeFileCheck)[keyof typeof AutoIntakeFileCheck];

export const AutoIntakeBlockCheck = {
  MATCHED_PROVIDER_NPI: {
    blockType: BlockType.PROVIDER,
    failureReason: "provider_npi_not_matched",
    passes: (has: HasValueAtPath) => has("npiNumber"),
  },
  MATCHED_REFERRING_PROVIDER_NPI: {
    blockType: BlockType.PROVIDER,
    blockKey: BlockKey.REFERRING_PROVIDER,
    failureReason: "referring_provider_npi_not_matched",
    passes: (has: HasValueAtPath) => has("npiNumber"),
  },
  GEOCODED_LOCATION: {
    blockType: BlockType.LOCATION,
    failureReason: "location_not_geocoded",
    passes: (has: HasValueAtPath) => has("placeId") && has("address.line1"),
  },
  GEOCODED_REFERRING_LOCATION: {
    blockType: BlockType.LOCATION,
    blockKey: BlockKey.REFERRING_LOCATION,
    failureReason: "referring_location_not_geocoded",
    passes: (has: HasValueAtPath) => has("placeId") && has("address.line1"),
  },
  PATIENT_PHONE_NUMBER: {
    blockType: BlockType.PATIENT,
    failureReason: "phone_number_missing",
    passes: (has: HasValueAtPath) => has("mobilePhoneNumber") || has("homePhoneNumber"),
  },
} as const;

export type AutoIntakeBlockCheck = (typeof AutoIntakeBlockCheck)[keyof typeof AutoIntakeBlockCheck];
export type FileEligibilityFailureReason = AutoIntakeFileCheck["failureReason"];
export type BlockEligibilityFailureReason = AutoIntakeBlockCheck["failureReason"];

export function getFileEligibilityFailureReason(
  file: Pick<File, "croppedPageIndices"> | null | undefined,
  checks: readonly AutoIntakeFileCheck[]
): FileEligibilityFailureReason | null {
  for (const check of checks) {
    if (!check.passes(file)) {
      return check.failureReason;
    }
  }

  return null;
}

export function getBlockEligibilityFailureReason(
  section: Section,
  blockConfig: BlockConfigFile | undefined | null,
  checks: readonly AutoIntakeBlockCheck[]
): BlockEligibilityFailureReason | null {
  const enrichedContent = section.enrichedContent;

  for (const blockKey of getBlockKeysForClassification(section.classification)) {
    if (!isBlockEnabled(blockConfig, blockKey)) {
      continue;
    }

    const initialReviewedFields = enrichedContent?.[blockKey]?.initialReviewedFields;
    const has = (path: string): boolean => hasValue(getNestedValue(initialReviewedFields, path));
    const blockType = getBlockTypeFromKey(blockKey);

    for (const check of checks) {
      const appliesToBlock = check.blockType === blockType && (!("blockKey" in check) || check.blockKey === blockKey);

      if (appliesToBlock && !check.passes(has)) {
        return check.failureReason;
      }
    }
  }

  return null;
}
